// ============================================================================
//  Relational data layer (Phase 2 of the relational migration).
//
//  Maps the app's in-memory state document <-> the normalized Supabase tables
//  defined in supabase-relational/schema.sql. Activated only when
//  REACT_APP_DB_MODE=relational (see dbService.js); otherwise the legacy
//  key-value store is used.
//
//  Fidelity rule: every row stores the FULL object in its `extra` jsonb column
//  AND populates typed columns for clean SQL/viewing. LOAD reconstructs purely
//  from `extra`, so the app behaves identically regardless of column mapping.
//  Child tables (document_items, journal_lines, …) are written for relational
//  querying but are not required to load.
// ============================================================================
import { supabase } from './config';
import { getTenantId } from '../tenant';

const n = v => (v === '' || v == null ? null : Number(v));
const d = v => (v ? v : null);           // date string 'YYYY-MM-DD' or null
const b = (v, dflt = true) => (v == null ? dflt : !!v);
const chunk = (arr, size = 500) => { const out = []; for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size)); return out; };

// Collections that map 1 row : 1 object, with typed column extractors.
const COLS = {
  parties: o => ({ name: o.name, type: o.type, category: o.category, gstin: o.gstin, pan: o.pan, state_gst_code: o.stateGstCode, state: o.state, currency: o.currency, phone: o.phone, email: o.email, address: o.address, opening_balance: n(o.openingBalance), balance_type: o.balanceType, linked_ledger: o.linkedLedger }),
  items: o => ({ name: o.name, item_code: o.itemCode, category: o.category, unit: o.unit, hsn_code: o.hsnCode, weight: n(o.weight), current_stock: n(o.currentStock), opening_stock: n(o.openingStock), purchase_rate: n(o.purchaseRate), sale_rate: n(o.saleRate), tax_percent: n(o.taxPercent), reorder_level: n(o.reorderLevel), basic_ledger: o.basicLedger, is_purchase_item: b(o.isPurchaseItem), is_sales_item: b(o.isSalesItem), is_active: b(o.isActive) }),
  accounts: o => ({ name: o.name, group: o.group, group_id: o.groupId, type: o.type, balance: n(o.balance), opening_balance: n(o.openingBalance), balance_type: o.balanceType }),
  accountGroups: o => ({ account_code: o.accountCode, account_name: o.accountName, group_name: o.groupName, parent_id: o.parentId, display_order: Number(o.displayOrder) || 0 }),
  taxes: o => ({ name: o.name, rate: n(o.rate), sgst_ledger: o.sgstLedger, cgst_ledger: o.cgstLedger, igst_ledger: o.igstLedger }),
  financialYears: o => ({ name: o.name, from_date: d(o.fromDate), to_date: d(o.toDate) }),
  prefixSettings: o => ({ txn_type: o.txnType, prefix: o.prefix, year_code: o.yearCode, start_no: Number(o.startNo) || 1, effective_from: d(o.effectiveFrom) }),
  salesInvoices: o => ({ invoice_no: o.invoiceNo, date: d(o.date), party_id: o.partyId, taxable: n(o.taxable), total_tax: n(o.totalTax), cgst: n(o.cgst), sgst: n(o.sgst), igst: n(o.igst), grand_total: n(o.grandTotal), round_off: n(o.roundOff), status: o.status, paid_amount: n(o.paidAmount), is_inter_state: b(o.isInterState, false), additional_charges: n(o.additionalCharges), bill_no: o.billNo, bill_date: d(o.billDate), due_date: d(o.dueDate), from_challan: o.fromChallan, financial_year: o.financialYear, narration: o.narration }),
  salesReturns: o => ({ return_no: o.returnNo, date: d(o.date), party_id: o.partyId, against_invoice: o.againstInvoice, taxable: n(o.taxable), total_tax: n(o.totalTax), grand_total: n(o.grandTotal), financial_year: o.financialYear, narration: o.narration }),
  deliveryChallans: o => ({ challan_no: o.challanNo, date: d(o.date), party_id: o.partyId, status: o.status, vehicle_no: o.vehicleNo, driver_name: o.driverName, financial_year: o.financialYear, narration: o.narration }),
  salesOrders: o => ({ order_no: o.orderNo, date: d(o.date), party_id: o.partyId, status: o.status, grand_total: n(o.grandTotal), financial_year: o.financialYear, narration: o.narration }),
  purchaseBills: o => ({ entry_no: o.entryNo, date: d(o.date), party_id: o.partyId, bill_no: o.billNo, bill_date: d(o.billDate), taxable: n(o.taxable), total_tax: n(o.totalTax), grand_total: n(o.grandTotal), status: o.status, paid_amount: n(o.paidAmount), due_date: d(o.dueDate), financial_year: o.financialYear, narration: o.narration }),
  purchaseReturns: o => ({ return_no: o.returnNo, date: d(o.date), party_id: o.partyId, against_invoice: o.againstInvoice, taxable: n(o.taxable), total_tax: n(o.totalTax), grand_total: n(o.grandTotal), financial_year: o.financialYear, narration: o.narration }),
  purchaseOrders: o => ({ order_no: o.orderNo, date: d(o.date), party_id: o.partyId, status: o.status, grand_total: n(o.grandTotal), financial_year: o.financialYear, narration: o.narration }),
  receipts: o => ({ receipt_no: o.receiptNo, date: d(o.date), party_id: o.partyId, amount: n(o.amount), mode: o.mode, bank_account: o.bankAccount, bank_details: o.bankDetails, cheque_no: o.chequeNo, against_invoice: o.againstInvoice, financial_year: o.financialYear, narration: o.narration }),
  payments: o => ({ payment_no: o.paymentNo, date: d(o.date), party_id: o.partyId, amount: n(o.amount), mode: o.mode, bank_account: o.bankAccount, bank_details: o.bankDetails, cheque_no: o.chequeNo, against_bill: o.againstBill, financial_year: o.financialYear, narration: o.narration }),
  journalEntries: o => ({ entry_no: o.entryNo, date: d(o.date), narration: o.narration, financial_year: o.financialYear }),
  expenses: o => ({ expense_no: o.expenseNo, date: d(o.date), expense_head: o.expenseHead, amount: n(o.amount), paid_from: o.paidFrom, bank_account: o.bankAccount, bill_ref: o.billRef, financial_year: o.financialYear, narration: o.narration }),
  contraEntries: o => ({ contra_no: o.contraNo, date: d(o.date), from_account: o.fromAccount, to_account: o.toAccount, amount: n(o.amount), financial_year: o.financialYear, narration: o.narration }),
  stockJournals: o => ({ journal_no: o.journalNo, date: d(o.date), item_id: o.itemId, qty: n(o.qty), type: o.type, reason: o.reason, financial_year: o.financialYear }),
  productions: o => ({ production_no: o.productionNo, date: d(o.date), batch_no: o.batchNo, financial_year: o.financialYear, narration: o.narration }),
  stockIssues: o => ({ issue_no: o.issueNo, date: d(o.date), consumption_head: o.consumptionHead, issued_to: o.issuedTo, financial_year: o.financialYear, narration: o.narration }),
  ledgerEntries: o => ({ date: d(o.date), ref_no: o.refNo, account: o.account, type: o.type, debit: n(o.debit), credit: n(o.credit) }),
};

const TABLE = {
  parties: 'parties', items: 'items', accounts: 'accounts', accountGroups: 'account_groups', taxes: 'taxes',
  financialYears: 'financial_years', prefixSettings: 'prefix_settings',
  salesInvoices: 'sales_invoices', salesReturns: 'sales_returns', deliveryChallans: 'delivery_challans', salesOrders: 'sales_orders',
  purchaseBills: 'purchase_bills', purchaseReturns: 'purchase_returns', purchaseOrders: 'purchase_orders',
  receipts: 'receipts', payments: 'payments', journalEntries: 'journal_entries', expenses: 'expenses', contraEntries: 'contra_entries',
  stockJournals: 'stock_journals', productions: 'productions', stockIssues: 'stock_issues', ledgerEntries: 'ledger_entries',
};

const COLLECTIONS = Object.keys(TABLE);
const ITEM_DOCS = ['salesInvoices', 'salesReturns', 'deliveryChallans', 'salesOrders', 'purchaseBills', 'purchaseReturns', 'purchaseOrders'];

// ─── Replace one collection's rows for this tenant ───────────────────────────
async function replaceCollection(key, list, tenant_id) {
  const table = TABLE[key];
  await supabase.from(table).delete().eq('tenant_id', tenant_id);
  const rows = (list || []).map(o => ({ tenant_id, id: o.id, ...COLS[key](o), extra: o }));
  for (const part of chunk(rows)) {
    if (part.length) { const { error } = await supabase.from(table).insert(part); if (error) throw error; }
  }
  // Child tables (relational mirror; not used on load)
  if (ITEM_DOCS.includes(key)) {
    await supabase.from('document_items').delete().eq('tenant_id', tenant_id).eq('doc_type', key);
    const lines = [];
    (list || []).forEach(o => (o.items || []).forEach((li, seq) => lines.push({
      tenant_id, doc_type: key, doc_id: o.id, seq, item_id: li.itemId, item_name: li.itemName,
      qty: n(li.qty), rate: n(li.rate), discount_percent: n(li.discountPercent), tax_percent: n(li.taxPercent),
      taxable_amount: n(li.taxableAmount), tax_amount: n(li.taxAmount), net_amount: n(li.netAmount), unit: li.unit, extra: li,
    })));
    for (const part of chunk(lines)) if (part.length) await supabase.from('document_items').insert(part);
  }
  if (key === 'journalEntries') {
    await supabase.from('journal_lines').delete().eq('tenant_id', tenant_id);
    const lines = [];
    (list || []).forEach(o => (o.lines || []).forEach((l, seq) => lines.push({ tenant_id, entry_id: o.id, seq, account: l.account, debit: n(l.debit), credit: n(l.credit), extra: l })));
    for (const part of chunk(lines)) if (part.length) await supabase.from('journal_lines').insert(part);
  }
  if (key === 'productions') {
    await supabase.from('production_lines').delete().eq('tenant_id', tenant_id);
    const lines = [];
    (list || []).forEach(o => {
      (o.inputs || []).forEach((l, seq) => lines.push({ tenant_id, production_id: o.id, role: 'input', seq, item_id: l.itemId, qty: n(l.qty), uom: l.uom, extra: l }));
      (o.outputs || []).forEach((l, seq) => lines.push({ tenant_id, production_id: o.id, role: 'output', seq, item_id: l.itemId, qty: n(l.qty), uom: l.uom, extra: l }));
    });
    for (const part of chunk(lines)) if (part.length) await supabase.from('production_lines').insert(part);
  }
  if (key === 'stockIssues') {
    await supabase.from('stock_issue_items').delete().eq('tenant_id', tenant_id);
    const lines = [];
    (list || []).forEach(o => (o.items || []).forEach((l, seq) => lines.push({ tenant_id, issue_id: o.id, seq, item_id: l.itemId, qty: n(l.qty), uom: l.uom, extra: l })));
    for (const part of chunk(lines)) if (part.length) await supabase.from('stock_issue_items').insert(part);
  }
}

async function loadCollection(key, tenant_id) {
  const { data, error } = await supabase.from(TABLE[key]).select('extra').eq('tenant_id', tenant_id);
  if (error) throw error;
  return (data || []).map(r => r.extra).filter(Boolean);
}

// ─── Public: load / save whole app state ─────────────────────────────────────
export async function loadAppDataRelational(defaults) {
  const tenant_id = getTenantId();
  try {
    const companyRes = await supabase.from('company').select('extra').eq('tenant_id', tenant_id).limit(1);
    const companyRow = companyRes.data && companyRes.data[0];
    if (!companyRow) return null; // not migrated yet
    const state = {
      company: { ...(defaults?.company || {}), ...(companyRow.extra || {}) },
    };
    const countersRes = await supabase.from('counters').select('data').eq('tenant_id', tenant_id).limit(1);
    const countersRow = countersRes.data && countersRes.data[0];
    state.counters = { ...(defaults?.counters || {}), ...((countersRow && countersRow.data) || {}) };

    for (const key of COLLECTIONS) {
      state[key] = await loadCollection(key, tenant_id);
    }
    return state;
  } catch (e) {
    console.error('Relational load failed:', e);
    return null;
  }
}

export async function saveAppDataRelational(state, prevState) {
  const tenant_id = getTenantId();
  // Company + counters (singletons)
  if (!prevState || prevState.company !== state.company) {
    const c = state.company || {};
    await supabase.from('company').upsert({ tenant_id, name: c.name, legal_name: c.legalName, gstin: c.gstin, pan: c.pan, state: c.state, city: c.city, email: c.email, phone: c.phone, logo: c.logo, signature: c.signature, extra: c, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id' });
  }
  if (!prevState || prevState.counters !== state.counters) {
    await supabase.from('counters').upsert({ tenant_id, data: state.counters || {}, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id' });
  }
  for (const key of COLLECTIONS) {
    if (!prevState || prevState[key] !== state[key]) {
      await replaceCollection(key, state[key], tenant_id);
    }
  }
}

// ─── Auth (users + log) ──────────────────────────────────────────────────────
export async function loadAuthUsersRelational() {
  const tenant_id = getTenantId();
  const { data, error } = await supabase.from('auth_users').select('extra').eq('tenant_id', tenant_id);
  if (error) { console.error('Relational auth load failed:', error); return null; }
  const users = (data || []).map(r => r.extra).filter(Boolean);
  return users.length ? users : null;
}

export async function saveAuthUsersRelational(users) {
  const tenant_id = getTenantId();
  await supabase.from('auth_users').delete().eq('tenant_id', tenant_id);
  const rows = (users || []).map(u => ({ tenant_id, id: u.id, username: u.username, full_name: u.fullName, role: u.role, password: u.password, is_active: u.isActive !== false, active_session: u.activeSession || null, modules: u.modules || [], extra: u }));
  for (const part of chunk(rows)) if (part.length) await supabase.from('auth_users').insert(part);
}

export async function loadAuthLogRelational() {
  const tenant_id = getTenantId();
  const { data, error } = await supabase.from('auth_log').select('extra').eq('tenant_id', tenant_id).order('id', { ascending: true });
  if (error) { console.error('Relational auth log load failed:', error); return []; }
  return (data || []).map(r => r.extra).filter(Boolean);
}

export async function appendAuthLogRelational(entry) {
  const tenant_id = getTenantId();
  await supabase.from('auth_log').insert({ tenant_id, at: entry.at || new Date().toISOString(), username: entry.username, action: entry.action, device: entry.device, extra: entry });
}
